/**
 * The business registration certificate (KYB) step's essence, kept apart from
 * the React component so it is testable without a browser.
 *
 * It uploads as a DOCUMENT (`evidence: "document"`): a PDF is accepted, and the
 * file is retained as a document image rather than biometric media. Then it
 * fires `BUSINESS_DOC_CAPTURED` with the file ref; the server checks the file
 * belongs to this case, points the flow at it and files it as a case document —
 * the same place a partner's or an operator's upload of it lands.
 */
import { blobToBase64 } from './capture';
import { uploadCollectionFile } from './client';
import type { CollectionFlowSnapshot } from './types';

export const BUSINESS_DOC_EVENT = 'BUSINESS_DOC_CAPTURED';

/** The server's document allowlist (`EvidenceKind::Document`), by MIME type. */
const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};
export const ACCEPTED_TYPES = Object.keys(EXTENSIONS).join(',');

/** `UploadLimits::max_document_bytes`. */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

export type BusinessDocumentProblem = 'unsupported' | 'tooLarge';

/**
 * Why a picked file cannot be sent, if it cannot. A courtesy only: the server
 * sniffs the bytes and enforces both rules itself.
 */
export function problemWith(file: Pick<File, 'size' | 'type'>): BusinessDocumentProblem | null {
  if (!(file.type in EXTENSIONS)) return 'unsupported';
  if (file.size > MAX_DOCUMENT_BYTES) return 'tooLarge';
  return null;
}

/** Upload a document file (PDF or image) to the case; returns its file id. */
export async function uploadDocumentFile(token: string, file: File, baseName: string): Promise<string> {
  const { file_id } = await uploadCollectionFile(token, {
    content_base64: await blobToBase64(file),
    content_type: file.type,
    file_name: `${baseName}.${EXTENSIONS[file.type] ?? 'bin'}`,
    evidence: 'document',
  });
  return file_id;
}

export async function submitBusinessDocument(
  token: string,
  file: File,
  postEvent: (token: string, type: string, payload?: unknown) => Promise<CollectionFlowSnapshot>,
): Promise<CollectionFlowSnapshot> {
  const file_id = await uploadDocumentFile(token, file, 'business-registration');
  return postEvent(token, BUSINESS_DOC_EVENT, { file_id });
}
